import { ConnectionStatus as ConnectionStatusType } from '../../services/signalr/connection';

interface ConnectionStatusProps {
  /** Current connection status */
  status: ConnectionStatusType;
  /** Optional additional class names */
  className?: string;
  /** Whether to show text label */
  showLabel?: boolean;
}

/**
 * Connection status indicator with animated dot
 * Shows SignalR connection state
 */
export function ConnectionStatus({
  status,
  className = '',
  showLabel = true,
}: ConnectionStatusProps) {
  const { color, label, animate } = getStatusConfig(status);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Status dot */}
      <span
        className={`
          w-2 h-2 rounded-full
          ${color}
          ${animate ? 'animate-pulse' : ''}
        `}
        aria-label={label}
      />
      {/* Label */}
      {showLabel && (
        <span className="text-gray-400 text-sm">{label}</span>
      )}
    </div>
  );
}

interface StatusConfig {
  color: string;
  label: string;
  animate: boolean;
}

function getStatusConfig(status: ConnectionStatusType): StatusConfig {
  switch (status) {
    case 'connected':
      return {
        color: 'bg-green-500',
        label: 'Connected',
        animate: false,
      };
    case 'connecting':
      return {
        color: 'bg-yellow-500',
        label: 'Connecting...',
        animate: true,
      };
    case 'reconnecting':
      return {
        color: 'bg-orange-500',
        label: 'Reconnecting...',
        animate: true,
      };
    case 'disconnected':
      return {
        color: 'bg-red-500',
        label: 'Disconnected',
        animate: false,
      };
  }
}

/**
 * Compact connection status (dot only) for headers
 */
export function ConnectionStatusDot({
  status,
  className = '',
}: Omit<ConnectionStatusProps, 'showLabel'>) {
  const { color, label, animate } = getStatusConfig(status);

  return (
    <span
      className={`
        inline-block w-2 h-2 rounded-full
        ${color}
        ${animate ? 'animate-pulse' : ''}
        ${className}
      `}
      title={label}
      aria-label={`Connection status: ${label}`}
    />
  );
}

/**
 * Connection status banner for showing warnings
 */
export function ConnectionStatusBanner({
  status,
  onRetry,
}: {
  status: ConnectionStatusType;
  onRetry?: () => void;
}) {
  if (status === 'connected') {
    return null;
  }

  const messages: Record<ConnectionStatusType, { title: string; message: string }> = {
    connected: { title: '', message: '' },
    connecting: {
      title: 'Connecting',
      message: 'Establishing real-time connection...',
    },
    reconnecting: {
      title: 'Connection Lost',
      message: 'Attempting to reconnect...',
    },
    disconnected: {
      title: 'Disconnected',
      message: 'Connection lost — retrying in background...',
    },
  };

  const { title, message } = messages[status];
  const bgColor =
    status === 'disconnected' ? 'bg-red-900/90' : 'bg-yellow-900/90';

  return (
    <div className={`${bgColor} px-4 py-2 border-b border-gray-700`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {status !== 'disconnected' && (
            <span className="animate-spin text-sm">⟳</span>
          )}
          <div>
            <span className="font-medium text-white">{title}</span>
            <span className="text-gray-300 text-sm ml-2">{message}</span>
          </div>
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            className={`px-3 py-1 text-sm text-white rounded transition-colors ${
              status === 'disconnected'
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-yellow-600 hover:bg-yellow-700'
            }`}
          >
            Reconnect Now
          </button>
        )}
      </div>
    </div>
  );
}

export default ConnectionStatus;
